import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import CancelSubscriptionNonCoolingOffPeriod from './NonCoolingOffPeriod';
import CancelSubscriptionCoolingOffPeriod from '../CoolingOffPeriod';
import type { CancelSubscriptionProduct } from './NonCoolingOffPeriod/constants';
import { flexProducts, normalizeProductFamily } from '../../constants/products';
import { subscriptionActions, channels, fulfillmentStatuses } from '../../constants';
import {
  useGetEntitlementsQuery,
  useUpdatePlanMutation,
  useDownsellPlanMutation,
} from '../../services/entitlementsApi';
import { selectIsFlexUser } from '../../services/responseUtility/entitlementsSelectors';
import useEntitlementsPolling from '../../hooks/flexHooks/useEntitlementsPolling';
import cancelSubscriptionTransition, { CancelSubscriptionStep } from '../../helpers/cancelSubscriptionTransition';
import getHomeUrl from '../../helpers/getHomeUrl';

type EntitlementsPlan = {
  planCode?: string;
  nextBillDate?: string | null;
  fulfillmentStatus?: string;
  trialLength?: number | null;
};

type EntitlementsResponse = {
  isInCoolingOffPeriod?: boolean;
  fulfillmentStatus?: string;
  plans?: EntitlementsPlan[];
  defermentInfo?: {
    type?: string;
  };
};

export default function CancelSubscription({ isWebview = false }: Readonly<{ isWebview?: boolean }>) {
  const { data, isLoading, isUninitialized } = useGetEntitlementsQuery(undefined);
  const entitlementsData = data as EntitlementsResponse | undefined;
  const isEntitlementsLoading = isUninitialized || isLoading;
  const isFlexUser = useSelector(selectIsFlexUser);
  const isInCoolingOffPeriod = entitlementsData?.isInCoolingOffPeriod ?? false;
  const hasPendingCancellation = [subscriptionActions.activation, subscriptionActions.cancellation].includes(
    entitlementsData?.defermentInfo?.type ?? ''
  );
  const plan = entitlementsData?.plans?.[0];
  const isAlreadyOnFree = plan?.planCode === flexProducts.free;
  const isSubscriptionNotFulfilled = entitlementsData?.fulfillmentStatus !== fulfillmentStatuses.succeeded;
  const isBillingDateToday = plan?.nextBillDate ? dayjs(plan.nextBillDate).isSame(dayjs(), 'day') : false;
  const dispatch = useDispatch();
  const history = useHistory();

  const onBack = () => {
    if (isWebview) {
      cancelSubscriptionTransition({ step: CancelSubscriptionStep.BACK });
      return;
    }

    if (globalThis.history.length > 1) {
      history.goBack();
      return;
    }

    dispatch(push('/'));
  };

  const onBackHome = () => {
    if (isWebview) {
      cancelSubscriptionTransition({ step: CancelSubscriptionStep.GO_HOME });
      return;
    }

    // routing to experian home page after thank you page
    globalThis.location.href = getHomeUrl(normalizedFamily, Boolean(plan?.trialLength));
  };

  const onDeferredCancel = async (onSuccess: () => void) => {
    if (isWebview) {
      cancelSubscriptionTransition({ step: CancelSubscriptionStep.CONFIRM_CANCELLATION });
    }

    const result = await updatePlan({ planCode: flexProducts.free, channel: upsellChannel });

    if ('error' in result) {
      throw new Error('Deferred flex cancellation failed');
    }

    if (!('data' in result) || !(result as any).data?.data?.[0]?.subscriptionId) {
      throw new Error('Deferred flex cancellation failed');
    }

    if (isWebview) {
      cancelSubscriptionTransition({ step: CancelSubscriptionStep.CANCELLATION_SUCCESS });
    }
    onSuccess();
  };

  const onImmediateCancel = async (onSuccess: () => void) => {
    if (isWebview) {
      cancelSubscriptionTransition({ step: CancelSubscriptionStep.CONFIRM_CANCELLATION });
    }

    const result = await downsellPlan({ planCode: flexProducts.free, channel: upsellChannel });

    if ('error' in result) {
      throw new Error('Immediate flex cancellation failed');
    }
    if (!('data' in result) || !(result as any).data?.data?.[0]?.subscriptionId) {
      throw new Error('Immediate flex cancellation failed');
    }

    if (isWebview) {
      cancelSubscriptionTransition({ step: CancelSubscriptionStep.CANCELLATION_SUCCESS });
    }

    onSuccess();
  };
  const upsellChannel = isWebview ? channels.app : channels.online;
  const normalizedFamily = normalizeProductFamily(plan?.planCode ?? '') as CancelSubscriptionProduct;

  const [updatePlan] = useUpdatePlanMutation();
  const [downsellPlan] = useDownsellPlanMutation();
  const [isCancelling, setIsCancelling] = useState(false);
  const { startPolling, pollingComplete } = useEntitlementsPolling(plan?.planCode ?? '');

  useEffect(() => {
    if (pollingComplete) {
      if (isWebview) {
        cancelSubscriptionTransition({ step: CancelSubscriptionStep.CANCELLATION_SUCCESS });
      }

      setIsCancelling(false);
    }
  }, [pollingComplete]);

  const handleConfirmCancellation = async () => {
    setIsCancelling(true);

    if (isWebview) {
      cancelSubscriptionTransition({ step: CancelSubscriptionStep.CONFIRM_CANCELLATION });
    }

    const result = await updatePlan({ planCode: flexProducts.free, channel: upsellChannel });
    if ('error' in result) {
      setIsCancelling(false);
    } else {
      startPolling();
    }
  };
  // Render nothing until entitlements have loaded. Without this, the cooling-off
  // flags default to false on the first render and we briefly flash the
  // non-cooling-off screen (and can redirect away) before the real values arrive.
  if (isEntitlementsLoading) {
    return null;
  }

  // Skip the redirect once the user has started/finished cancellation, so the
  // Thank You screen can render even though entitlements now reflect the cancel.
  if (
    !isCancelling &&
    !pollingComplete &&
    (!isFlexUser || isAlreadyOnFree || hasPendingCancellation || isBillingDateToday || isSubscriptionNotFulfilled)
  ) {
    if (isWebview) {
      cancelSubscriptionTransition({ step: CancelSubscriptionStep.BACK });
      return null;
    }

    dispatch(push('/'));
    return null;
  }

  if (isInCoolingOffPeriod) {
    return (
      <CancelSubscriptionCoolingOffPeriod
        subscriptionFamily={normalizedFamily}
        nextBillingDate={plan?.nextBillDate}
        onBack={onBack}
        onDeferredCancel={onDeferredCancel}
        onImmediateCancel={onImmediateCancel}
        onBackHome={onBackHome}
        isWebview={isWebview}
        isFlexFlow={true}
      />
    );
  }

  return (
    <CancelSubscriptionNonCoolingOffPeriod
      currentSubscription={normalizedFamily}
      nextBillingDate={plan?.nextBillDate}
      onBack={onBack}
      onBackHome={onBackHome}
      isWebview={isWebview}
      isCancelling={isCancelling}
      pollingComplete={pollingComplete}
      onConfirm={handleConfirmCancellation}
    />
  );
}
